import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import type { ProviderPairCounterpartStatus } from './providerPairPresentation'

export enum ProviderPairAccess {
  WRITE = 'write',
  READ = 'read',
}

// UI-layer classification; `AI` is intentionally not the backend `ai_coach` value.
export enum MessengerParticipant {
  PATIENT = 'patient',
  GUARDIAN = 'guardian',
  AI = 'ai',
  PROVIDER_PAIR = 'provider-pair',
}

type ParticipantConfig = {
  canSendMessages: boolean
  showInputActions: boolean
  showUnreadBadge: boolean
  requiresExistingConversation: boolean
  disableDrafts: boolean
}

export const PARTICIPANT_CONFIG: Record<MessengerParticipant, ParticipantConfig> = {
  [MessengerParticipant.PATIENT]: {
    canSendMessages: true,
    showInputActions: true,
    showUnreadBadge: true,
    requiresExistingConversation: false,
    disableDrafts: false,
  },
  [MessengerParticipant.GUARDIAN]: {
    canSendMessages: true,
    showInputActions: false,
    showUnreadBadge: true,
    requiresExistingConversation: false,
    disableDrafts: true,
  },
  [MessengerParticipant.AI]: {
    canSendMessages: false,
    showInputActions: false,
    showUnreadBadge: false,
    requiresExistingConversation: true,
    disableDrafts: true,
  },
  [MessengerParticipant.PROVIDER_PAIR]: {
    canSendMessages: true,
    showInputActions: false,
    showUnreadBadge: true,
    requiresExistingConversation: true,
    disableDrafts: false,
  },
}

export type MessengerConversation = Pick<
  ConversationObject,
  'conversation_attributes' | 'conversation_id' | 'db_conversation_id' | 'patient_lyra_id' | 'provider_lyra_id'
> &
  Partial<Pick<ConversationObject, 'conversation_date_created'>> & {
    counterpartProviderId?: string
    // The caller's own unread count. Pairs don't use the legacy unread_provider_messages_count.
    providerPairUnreadCount?: number
  }

export type MessengerThread = {
  // Participant is the UI role; id is the stable identity used for selection. AI conversations
  // share the AI role but remain separate threads when both coaching types are present.
  id: string
  participant: MessengerParticipant
  conversationType: CONVERSATION_TYPE
  // null when eligible but not yet created. The first send creates it, via useEnsureConversation.
  conversationId: string | null
  conversation: ConversationObject | null
  recipientId: string
  recipientName: string
  tabLabel: string
  unreadCount: number
  canSendMessages: boolean
  showInputActions: boolean
  disableDrafts: boolean
  recipientIsDirectAccess?: boolean
  // Set only for provider-pair threads.
  providerPairAccess?: ProviderPairAccess
  // Set only when a supported source explicitly identifies the counterpart's status.
  providerPairStatus?: ProviderPairCounterpartStatus
  // Program names from this conversation’s associated care-history episodes.
  providerPairProgramNames?: string[]
}
